import { useEffect } from "react";
import {
  ArrowRight,
  Check,
  Calendar,
  Megaphone,
  Inbox,
  Workflow,
  FileText,
  LayoutDashboard,
  Activity,
  Code2,
  Compass,
  Cog,
  Rocket,
  Music2,
  Palette,
  Mic,
  Building,
  Trophy,
  GraduationCap,
  Briefcase,
  CalendarDays,
  Star,
  Database,
  Network,
  Banknote,
  Layers,
  Sparkles,
  Target,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import IntakeForm from "@/components/IntakeForm";

const CAL_URL = "https://cal.com/billy-sadia-gv78xs/discovery-call?overlayCalendar=true";

const PROBLEM_POINTS = [
  { icon: Inbox, title: "Lost in social inboxes", desc: "Bookings, sponsor briefs, and partnership requests buried in Instagram DMs, WhatsApp threads, and email subfolders. Every missed reply is a missed opportunity." },
  { icon: FileText, title: "Spreadsheet-driven operations", desc: "Pipelines tracked in shared sheets, calendars that don't talk to each other, and version-control by who-edited-last. Brittle, manual, slow to scale." },
  { icon: Workflow, title: "Manual follow-up", desc: "Every inquiry waits in a human queue. Response times stretch from minutes to days. Warm leads cool before they ever reach a calendar." },
  { icon: Network, title: "Disconnected tools", desc: "Twelve apps that don't share data. CRM here, calendar there, payments somewhere else. No single source of truth and no real automation." },
  { icon: Cog, title: "Informal processes", desc: "Operations live in someone's head. Onboard a teammate and you copy a playbook from memory. Scale becomes impossible without burnout." },
  { icon: Activity, title: "No operational visibility", desc: "How many open inquiries, active deals, confirmed bookings, paid invoices this month? Best guess. Decisions get made on vibes, not data." },
];

const WHAT_WE_BUILD = [
  { icon: Calendar, code: "01 / Booking", title: "Booking Infrastructure", desc: "Performance bookings, event bookings, consultations, workshops — handled by automated calendar flows with deposits, confirmations, and capacity rules." },
  { icon: Megaphone, code: "02 / Partnerships", title: "Partnership Infrastructure", desc: "Sponsor inquiries, brand collaborations, and partnership pipelines structured into a deal-flow board with NDAs, briefs, and post-campaign reporting." },
  { icon: Database, code: "03 / Lead", title: "Lead Infrastructure", desc: "Lead capture, CRM integration, scoring, and follow-up sequences. One source of truth across every inbound channel your organization touches." },
  { icon: Network, code: "04 / Comms", title: "Communication Infrastructure", desc: "WhatsApp routing, intelligent auto-replies, and inquiry management routed in parallel to inbox, CRM, and Slack — no inquiry sits silent." },
  { icon: LayoutDashboard, code: "05 / Operations", title: "Operations Infrastructure", desc: "Dashboards, workflows, and centralized management surfaces. Every revenue surface visible in real-time, every team operating from the same data." },
  { icon: Banknote, code: "06 / Monetization", title: "Monetization Infrastructure", desc: "Systems that turn attention into repeatable revenue: rate cards, contract automation, payment flows, and recurring partnership offers." },
];

const STEPS = [
  { n: "01", icon: Compass, title: "Strategy", desc: "Map every opportunity surface, current bottleneck, and operational gap. You leave step one with a written infrastructure brief — even if you go no further." },
  { n: "02", icon: Layers, title: "Architecture", desc: "Design the systems architecture: data model, integration map, automation graph, permissions, and ownership. Documented before any tool is configured." },
  { n: "03", icon: Code2, title: "Build", desc: "Implement and integrate every tool in the stack — booking, CRM, routing, dashboards. Each component tested against scenarios from your real operation." },
  { n: "04", icon: Cog, title: "Automation", desc: "Reduce manual workload through orchestrated workflows. Hand-offs that previously required a human get done by the system, with humans only in approval seats." },
  { n: "05", icon: Rocket, title: "Scale", desc: "Documented runbooks, team training, and a 30-day stabilization window. You're left with a foundation that grows with your organization, not against it." },
];

const USE_CASES = [
  { icon: Palette, title: "Creative organizations", desc: "Studios, design collectives, content production houses." },
  { icon: Music2, title: "Dance collectives", desc: "Performance bookings, class operations, sponsor pipelines." },
  { icon: Trophy, title: "Sports organizations", desc: "Team operations, partnership deals, fan-engagement systems." },
  { icon: GraduationCap, title: "Education brands", desc: "Course operations, cohort management, enrollment pipelines." },
  { icon: Mic, title: "Content creators", desc: "Brand-deal flow, rate cards, contract automation." },
  { icon: Sparkles, title: "Influencer brands", desc: "Sponsor inbound, campaign coordination, monetization stacks." },
  { icon: Calendar, title: "Events businesses", desc: "Ticketing, sponsor activation, multi-event operations." },
  { icon: Briefcase, title: "Service businesses", desc: "Client intake, project pipelines, retainer billing automation." },
];

const PACKAGES = [
  {
    name: "Foundation System",
    tagline: "Build operational structure",
    description: "One critical surface, engineered into a working system. The fastest way to remove your most painful bottleneck.",
    items: [
      "1 infrastructure module",
      "Inbound capture + automated response",
      "CRM contact log + lead tracking",
      "Single-channel routing",
      "Documentation & team handover",
    ],
    bestFor: "Emerging organizations & solo operators",
    highlight: false,
  },
  {
    name: "Growth System",
    tagline: "Lead management, partnerships, and automation",
    description: "Multi-module operating layer. The everyday infrastructure for organizations scaling past founder bottleneck.",
    items: [
      "Up to 3 infrastructure modules",
      "Booking + Lead + Communication systems",
      "Partnership pipeline + media kit",
      "Centralized operations dashboard",
      "Automated workflows + integrations",
      "Team training + 30-day stabilization",
    ],
    bestFor: "Established organizations & growing brands",
    highlight: true,
  },
  {
    name: "Business Operating System",
    tagline: "Complete infrastructure ecosystem",
    description: "Every revenue surface engineered. Every workflow automated. Every dashboard live. Your full operating system.",
    items: [
      "All 6 infrastructure modules",
      "Multi-channel routing (WA + IG + Email + Slack)",
      "Sponsorship pipeline + contract automation",
      "AI triage + smart-reply layer",
      "Custom dashboards + revenue analytics",
      "Priority retainer support",
    ],
    bestFor: "Organizations operating at scale",
    highlight: false,
  },
];

const WHY_US = [
  { icon: Layers, title: "Systems-first thinking", desc: "Every engagement opens with architecture, not tools. We design the data model and automation graph before a single account is created." },
  { icon: Compass, title: "Operational clarity", desc: "You walk away knowing exactly who does what, when, and how the system supports them. No mystery process, no agency lock-in." },
  { icon: Banknote, title: "Revenue-focused infrastructure", desc: "Every module justifies itself against a revenue surface: bookings, partnerships, retention, or recurring deals. We build for ROI, not novelty." },
  { icon: Cog, title: "Automation expertise", desc: "Deep fluency across the no-code/low-code stack: n8n, Make, HubSpot, Notion, Retool, OpenAI, and custom APIs. Stack chosen per engagement." },
  { icon: Rocket, title: "Scalable processes", desc: "Systems documented, automations versioned, runbooks handed over. Your operation grows without rebuilding from scratch every quarter." },
];

const FAQS = [
  { q: "Is this a web design agency?", a: "No. We design and build the business infrastructure behind organizations — booking systems, partnership pipelines, CRM, routing, dashboards. A website may be part of the surface, but the operating system is the actual deliverable." },
  { q: "What does an engagement look like?", a: "Strategy → Architecture → Build → Automation → Scale. A typical Foundation System ships in 1–2 weeks. Growth System in 3–4 weeks. Business Operating System in 5–8 weeks. Every engagement begins with a written architecture document." },
  { q: "Why don't you publish fixed prices?", a: "Every organization has a different stack, scope, and operational complexity. Investment is custom and quoted after we review your intake form and run a strategy call. Engagements typically begin in the low four figures and scale based on modules." },
  { q: "Do we own everything you build?", a: "Yes. Every account, automation, dashboard, and template is built in your tenant on tools you own. Credentials, source documents, and runbooks transferred at deploy. No agency lockout." },
  { q: "What tools do you build with?", a: "API-native and integration-first: Cal.com, HubSpot / Notion / Airtable, WhatsApp Cloud API, n8n / Make, Retool, Stripe, OpenAI. Stack is chosen per engagement, not pushed by vendor partnership." },
  { q: "Do you offer ongoing optimization?", a: "Optional retainer post-deploy: conversion tuning, automation expansion, new module additions. Most clients move from build → 30-day stabilization → light retainer once the operation is stable." },
  { q: "How do payments work?", a: "Bank transfer, Wise, and most international payment methods. Invoice-based engagement — no automated checkout. Every project starts with a written scope and a fixed-price proposal." },
  { q: "Are you a large agency?", a: "Founder-led studio, supported by a vetted specialist network when needed. You work directly with the operator who architects the systems. The trade-off is a limited number of engagements per quarter." },
  { q: "What happens after I book a strategy call?", a: "We diagnose your operation, map opportunities, and within one business day you receive a custom infrastructure brief, scope agreement, and fixed-price proposal. Build starts within 48 business hours of paid invoice." },
  { q: "Do you offer refunds?", a: "Refunds available before production starts. Once architecture and build work has begun, payments are non-refundable. Full details on the Refund Policy page." },
];

const FOUNDER = {
  name: "Billy Sadia",
  role: "Founder · Brand-in-a-Box",
  photo: "https://customer-assets.emergentagent.com/job_brand-authority-21/artifacts/m27ohiuc_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2008_48_01%20PM.png",
  story: [
    "I'm Billy. I run Brand-in-a-Box as a focused infrastructure studio — built around one belief: the next generation of organizations won't be defined by their reach, but by the systems that turn that reach into structured revenue.",
    "I work with creative organizations, cultural collectives, sports brands, educators, and service businesses that have grown past the limits of social-media inboxes and shared spreadsheets. My job is to architect the operating system that lets them keep growing without breaking the team that runs it.",
    "Every engagement is led personally — I write the architecture document, design the automations, sit in the WhatsApp thread until your operation runs without depending on you to remember every step.",
  ],
  pillars: [
    "Founder personally architects every engagement",
    "Documented architecture before tools touched",
    "Fixed-price scope — no surprise invoices",
    "30-day post-deploy stabilization included",
  ],
};

export default function Home() {
  useEffect(() => {
    document.title = "Brand-in-a-Box | We Build the Systems Behind Growth";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "We design and build the business infrastructure — booking systems, partnership pipelines, lead management, automation, and operations — that helps organizations turn attention into structured revenue.",
      );
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="home-page" className="bg-[#05060c] text-slate-100 pb-24 lg:pb-0">
      {/* ---------------- HERO ---------------- */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
        <div className="absolute inset-0 bg-aurora" aria-hidden />
        <div className="absolute inset-0 bg-vignette" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 md:pt-32 pb-24 md:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div data-testid="hero-badge" className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 pulse-glow" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
                </span>
                <span className="text-[11px] font-mono tracking-widest uppercase text-cyan-200">
                  Infrastructure Studio · Now booking Q1
                </span>
              </div>

              <h1 data-testid="hero-headline" className="mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[68px] font-black tracking-tighter leading-[1.02]">
                Most Organizations Don't Have an Attention Problem.{" "}
                <span className="gradient-text-cyan">They Have an Infrastructure Problem.</span>
              </h1>

              <div className="mt-7 space-y-3 max-w-2xl">
                <p data-testid="hero-subheadline" className="text-lg md:text-xl text-slate-300 leading-relaxed">
                  Attention creates opportunities. Infrastructure turns opportunities into revenue.
                </p>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed">
                  We design and build booking systems, partnership pipelines, lead management workflows, automation, and operational infrastructure that help organizations scale with confidence.
                </p>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                  data-testid="hero-cta-primary"
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-8 py-6 text-base glow-cyan transition-all hover:-translate-y-0.5"
                >
                  <CalendarDays className="h-4 w-4" /> Book a Strategy Call
                </a>
                <button
                  data-testid="hero-cta-secondary"
                  onClick={() => scrollTo("modules")}
                  className="inline-flex items-center justify-center gap-2 rounded-full glass hover:bg-white/[0.06] text-white font-semibold px-8 py-6 text-base transition-all"
                >
                  Explore Infrastructure Systems <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 max-w-2xl">
                {[
                  "Documented architecture",
                  "Fixed-price scope",
                  "48-hour build start",
                  "30-day stabilization",
                ].map((b) => (
                  <div
                    key={b}
                    data-testid={`hero-trust-${b.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-start gap-2 text-slate-400"
                  >
                    <span className="text-cyan-400 font-mono text-xs mt-0.5">→</span>
                    <span className="text-xs font-medium">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Architecture diagram visual */}
            <div className="lg:col-span-5 fade-up" style={{ animationDelay: "150ms" }}>
              <div className="relative">
                <div className="absolute -inset-12 bg-cyan-500/10 rounded-full blur-3xl" aria-hidden />
                <div className="relative rounded-3xl glass-strong p-6 md:p-8">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-rose-400" />
                      <div className="h-2 w-2 rounded-full bg-amber-400" />
                      <div className="h-2 w-2 rounded-full bg-emerald-400" />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                      operating-system.live
                    </span>
                  </div>

                  <div className="space-y-3">
                    {[
                      { label: "Active Pipelines", value: "47", trend: "+12 this week", color: "cyan" },
                      { label: "Bookings Confirmed", value: "$24.4K", trend: "8 events", color: "blue" },
                      { label: "Partnership Deals", value: "5 active", trend: "$38K MRR", color: "purple" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 flex items-center justify-between">
                        <div>
                          <p className="text-[10px] font-mono tracking-widest uppercase text-slate-500">
                            {s.label}
                          </p>
                          <p className="mt-1 text-2xl font-extrabold text-white tracking-tight">
                            {s.value}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-semibold ${s.color === "cyan" ? "text-cyan-300" : s.color === "blue" ? "text-blue-300" : "text-purple-300"}`}
                        >
                          {s.trend}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 pt-5 border-t border-white/[0.06]">
                    <p className="text-[10px] font-mono tracking-widest uppercase text-slate-500 mb-3">
                      System Health
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {["Booking", "CRM", "Routing"].map((m) => (
                        <div key={m} className="flex items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-glow" />
                          <span className="text-[11px] font-mono text-slate-300">{m}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- THE PROBLEM ---------------- */}
      <section id="problem" data-testid="problem-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-rose-400">
              The Problem
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Opportunities don't disappear because you missed them. They disappear because nothing <span className="text-rose-400">caught them</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Most organizations lose more revenue to the gaps between their tools than to any competitor. The leak isn't visible — until you look at the systems.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROBLEM_POINTS.map((p) => (
              <div
                key={p.title}
                data-testid={`problem-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl glass p-7 lift-on-hover"
              >
                <div className="h-11 w-11 rounded-xl bg-rose-500/10 text-rose-400 inline-flex items-center justify-center shrink-0 border border-rose-500/20">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-bold tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- THE SHIFT ---------------- */}
      <section data-testid="insight-section" className="relative py-24 md:py-32 border-t border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 bg-aurora opacity-50" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
            The Shift
          </span>
          <p className="mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            Attention Is Not Enough.{" "}
            <span className="gradient-text-cyan">Organizations grow when systems support growth.</span>
          </p>
          <p className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mx-auto">
            Visibility creates the inquiry. Infrastructure creates the deal. The organizations that scale aren't the ones with the loudest channels — they're the ones with the operating systems that turn every inbound into a booking, partnership, or recurring revenue line.
          </p>
          <p className="mt-6 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl mx-auto">
            We build the systems behind that growth.
          </p>
        </div>
      </section>

      {/* ---------------- WHAT WE BUILD ---------------- */}
      <section id="modules" data-testid="modules-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-7">
              <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
                What We Build
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Six layers of infrastructure. One operating system.
              </h2>
            </div>
            <p className="lg:col-span-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Deploy a single layer to remove a bottleneck. Deploy all six to run as a fully structured organization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_BUILD.map((m) => (
              <div
                key={m.title}
                data-testid={`module-card-${m.code.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-2xl glass p-7 lift-on-hover relative overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-cyan-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 rounded-xl bg-cyan-500/10 text-cyan-300 inline-flex items-center justify-center border border-cyan-500/20">
                      <m.icon className="h-5 w-5" />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                      {m.code}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold tracking-tight text-white">{m.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section id="process" data-testid="process-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
              How It Works
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Strategy → Architecture → Build → Automation → Scale.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Every step produces an artifact you keep — architecture diagrams, integration maps, runbooks, training docs — even if you choose not to continue.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`process-step-${s.n}`}
                className="rounded-2xl glass p-6 lift-on-hover"
              >
                <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                  Step {s.n}
                </span>
                <div className="mt-3 h-10 w-10 rounded-lg bg-cyan-500/10 text-cyan-300 inline-flex items-center justify-center border border-cyan-500/20">
                  <s.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-white tracking-tight">{s.title}</h3>
                <p className="mt-3 text-xs md:text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- WHO WE WORK WITH ---------------- */}
      <section id="about" data-testid="audience-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
              Who We Work With
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Built for organizations that have already earned the attention.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Our work begins where your visibility ends. We turn audience, inquiries, and opportunities into structured operations.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {USE_CASES.map((u) => (
              <div
                key={u.title}
                data-testid={`audience-card-${u.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl glass p-6 lift-on-hover"
              >
                <div className="h-10 w-10 rounded-lg bg-purple-500/10 text-purple-300 inline-flex items-center justify-center border border-purple-500/20">
                  <u.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-4 text-sm md:text-base font-bold text-white tracking-tight">{u.title}</h3>
                <p className="mt-2 text-xs text-slate-400 leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PACKAGES ---------------- */}
      <section id="packages" data-testid="packages-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
              Infrastructure Packages
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Three engagement tiers. Custom investment per scope.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              We don't publish fixed prices because no two organizations carry the same operational weight. Investment is quoted after we review your intake and run a strategy call.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PACKAGES.map((p) => (
              <div
                key={p.name}
                data-testid={`package-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative rounded-3xl p-8 md:p-10 transition-all duration-300 ${
                  p.highlight
                    ? "glass-strong border border-cyan-500/30 glow-cyan-soft md:-translate-y-2"
                    : "glass lift-on-hover"
                }`}
              >
                {p.highlight && (
                  <span
                    data-testid="package-most-popular-badge"
                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-cyan-400 text-slate-950 text-xs font-bold px-3 py-1 shadow-md"
                  >
                    <Star className="h-3 w-3" /> Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-extrabold tracking-tight text-white">{p.name}</h3>
                <p className={`mt-1 font-mono text-[11px] font-semibold tracking-widest uppercase ${p.highlight ? "text-cyan-300" : "text-cyan-400"}`}>
                  {p.tagline}
                </p>
                <p
                  data-testid={`package-range-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-white"
                >
                  Custom Investment
                </p>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{p.description}</p>
                <ul className="mt-6 space-y-3">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-cyan-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/[0.06]">
                  <p className="text-[10px] font-mono tracking-widest uppercase text-slate-500 mb-2">Best for</p>
                  <p className="text-sm font-semibold text-white">{p.bestFor}</p>
                </div>
                <a
                  data-testid={`package-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full font-semibold h-12 px-6 transition-all ${
                    p.highlight
                      ? "bg-cyan-400 hover:bg-cyan-300 text-slate-950 glow-cyan"
                      : "bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/10"
                  }`}
                >
                  <CalendarDays className="h-4 w-4" /> Book a Strategy Call
                </a>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl mx-auto rounded-2xl glass px-6 py-5 text-center">
            <p className="text-sm md:text-base text-slate-300 leading-relaxed">
              Every engagement is customized based on modules deployed, integrations required, and operational complexity. Final investment is quoted after architecture review.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- WHY CLIENTS CHOOSE US ---------------- */}
      <section data-testid="why-us-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
              Why Clients Choose Us
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Architecture, not aesthetics.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Five principles every engagement is held to — regardless of stack, scale, or vertical.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((w) => (
              <div
                key={w.title}
                data-testid={`why-us-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl glass p-7 lift-on-hover"
              >
                <div className="h-11 w-11 rounded-xl bg-blue-500/10 text-blue-300 inline-flex items-center justify-center border border-blue-500/20">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-lg font-bold tracking-tight text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FOUNDER ---------------- */}
      <section data-testid="founder-bio-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-6 bg-cyan-500/15 rounded-3xl blur-3xl" aria-hidden />
                <div className="relative rounded-3xl overflow-hidden border border-white/10 glow-cyan-soft">
                  <img
                    src={FOUNDER.photo}
                    alt={`${FOUNDER.name}, ${FOUNDER.role}`}
                    className="w-full h-[520px] object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent p-6">
                    <p className="text-white font-bold tracking-tight text-lg">{FOUNDER.name}</p>
                    <p className="text-cyan-300 text-xs font-mono tracking-widest uppercase mt-1">{FOUNDER.role}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
                Founder
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
                A builder, focused on the systems behind growth.
              </h2>
              {FOUNDER.story.map((para, i) => (
                <p key={i} className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">{para}</p>
              ))}
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FOUNDER.pillars.map((pillar) => (
                  <li key={pillar} className="flex items-start gap-2 text-slate-300 text-sm">
                    <Check className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
                    <span>{pillar}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                  data-testid="founder-bio-cta-discovery-call"
                  href={CAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-7 py-6 text-sm glow-cyan-soft"
                >
                  <CalendarDays className="h-4 w-4" /> Book a Strategy Call
                </a>
                <button
                  data-testid="founder-bio-cta-proposal"
                  onClick={() => scrollTo("proposal")}
                  className="inline-flex items-center justify-center gap-2 rounded-full glass hover:bg-white/[0.06] text-white font-semibold px-7 py-6 text-sm transition-all"
                >
                  Submit Intake Form <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FAQ ---------------- */}
      <section id="faq" data-testid="faq-section" className="relative py-24 md:py-32 border-t border-white/[0.06]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">FAQ</span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Common questions about how we engage.
            </h2>
          </div>
          <Accordion
            type="single"
            collapsible
            className="mt-12 divide-y divide-white/[0.06] border-y border-white/[0.06]"
            data-testid="faq-accordion"
          >
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-0" data-testid={`faq-item-${i + 1}`}>
                <AccordionTrigger className="text-left text-base md:text-lg font-semibold text-white hover:text-cyan-300 py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm md:text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <p className="text-sm text-slate-500">Review our <a href="/privacy-policy" className="text-cyan-300 hover:text-cyan-200">Privacy Policy</a> and <a href="/terms-of-service" className="text-cyan-300 hover:text-cyan-200">Terms of Service</a>.</p>
          </div>
        </div>
      </section>

      {/* ---------------- INTAKE FORM ---------------- */}
      <section id="proposal" data-testid="proposal-section" className="relative py-24 md:py-32 border-t border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 bg-aurora opacity-40" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-cyan-400">
              Intake
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Prefer async? Submit the intake form.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-400 leading-relaxed">
              Tell us about your operation. Within one business day you'll receive a custom infrastructure brief, scope agreement, and fixed-price proposal. Or skip this and book a strategy call instead.
            </p>
          </div>
          <div className="mt-12">
            <IntakeForm />
          </div>
          <div className="mt-10 text-center">
            <p className="text-sm text-slate-500 mb-3">Prefer to talk first?</p>
            <a
              data-testid="proposal-discovery-call"
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-6 py-3 text-sm glow-cyan-soft transition-all"
            >
              <CalendarDays className="h-4 w-4" /> Book a Strategy Call
            </a>
          </div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section data-testid="final-cta-section" className="relative py-24 md:py-32 border-t border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 bg-grid-fine opacity-50" aria-hidden />
        <div className="absolute inset-0 bg-aurora opacity-60" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[1.05]">
            Build the Infrastructure Behind Your{" "}
            <span className="gradient-text-cyan">Next Stage of Growth.</span>
          </h2>
          <p className="mt-7 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Book a 30-minute strategy call. We'll map your bottlenecks, identify the highest-leverage system to build first, and send you a written infrastructure brief within one business day.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              data-testid="final-cta-button"
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-10 py-6 text-base glow-cyan transition-all hover:-translate-y-0.5"
            >
              <CalendarDays className="h-4 w-4" /> Book a Strategy Call
            </a>
            <button
              data-testid="final-cta-discovery-call"
              onClick={() => scrollTo("proposal")}
              className="inline-flex items-center justify-center gap-2 rounded-full glass hover:bg-white/[0.06] text-white font-semibold px-10 py-6 text-base transition-all"
            >
              Submit Intake Form <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-8 text-sm text-slate-500">
            Business infrastructure for organizations, creators, cultural brands & service businesses.
          </p>
        </div>
      </section>

      {/* ---------------- STICKY MOBILE CTA ---------------- */}
      <div data-testid="sticky-mobile-cta" className="lg:hidden fixed bottom-4 left-4 right-4 z-40 pointer-events-none">
        <a
          href={CAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto w-full inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-6 py-4 glow-cyan transition-all"
        >
          <CalendarDays className="h-4 w-4" /> Book a Strategy Call
        </a>
      </div>
    </div>
  );
}
